import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useRecoilState, useResetRecoilState } from "recoil";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import humanMaleFemale from "@iconify/icons-mdi/human-male-female";
import {
  useMyHrContractsByEmployee,
  useMyHrSpousesData as useMyHrSpousesDataDI,
} from "my-hr/hooks";
import MyTaxApplicationCardInfoItem from "my-tax/components/application-card-info-item/application-card-info-item";
import ApplicationCollapseHeader from "my-tax/components/application-collapse-header";
import MyTaxApplicationHeader from "my-tax/components/application-header/application-header";
import MyTaxFirstMarriageTaxCreditApplicationPrint from "my-tax/components/first-marriage-tax-credit-application-print/first-marriage-tax-credit-application-print";
import MyTaxModifiedStatementCheck from "my-tax/components/modified-statement-check/modified-statement-check";
import MyTaxSpouseCollapse from "my-tax/components/spouse-collapse/spouse-collapse";
import TaxCreditApplicationButtonBars from "my-tax/components/tax-credit-application-buttonbars/tax-credit-application-buttonbars";
import MyTaxCreditApplicationCheckboxLine from "my-tax/components/tax-credit-application-checkbox-line/tax-credit-application-checkbox-line";
import MyTaxCreditApplicationErrorBox from "my-tax/components/tax-credit-application-error-box/tax-credit-application-error-box";
import { myTaxFirstMarriageTaxCreditInformationPageRoute } from "my-tax/configs/routes";
import { NOT_PROVIDED } from "my-tax/constants";
import { MY_TAX_GET_FIRST_MERRIED_PERSON_DECLARATION } from "my-tax/graphql";
import { MY_TAX_CREATE_FIRST_MARRIED_PERSON_DECLARATION } from "my-tax/graphql";
import { firstApplyInTaxYearOrToWithDrawal } from "my-tax/states/first-apply-in-tax-year-or-to-with-drawal";
import { taxDeclarationFirstMarriedPerson } from "my-tax/states/tax-declaration-first-married-person";
import { OcCard, OcDatePicker, OcDownload, OcMonthSelect } from "oc/components";
import { useOcCurrentUserState, useOcSnackbar } from "oc/hooks";
import { getDateFormatByLocale } from "oc/utils";

type UseFormDataProps = {
  _id: number | string | undefined;
  setSelected: (params: any) => void;
};

const useFormData = ({ _id, setSelected }: UseFormDataProps) => {
  const { t } = useTranslation();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [disabledButton, setDisabledButton] = useState(false);
  const [states, setStates] = useRecoilState(taxDeclarationFirstMarriedPerson);
  const resetStates = useResetRecoilState(taxDeclarationFirstMarriedPerson);

  const { data, loading, error } = useQuery(
    MY_TAX_GET_FIRST_MERRIED_PERSON_DECLARATION,
    {
      fetchPolicy: "cache-and-network",
      skip: !_id,
      variables: {
        _id: _id !== undefined ? Number.parseInt(_id as string, 10) : undefined,
      },
    },
  );

  const result = data?.taxDeclarationFirstMarriedPerson;

  // Sync query result to Recoil state (replaces deprecated onCompleted)
  useEffect(() => {
    if (!loading && result) {
      const newStates = { ...result };
      newStates.dateOfDeclaration = new Date(result?.dateOfDeclaration);
      if (newStates.applyFromMonth != null) {
        newStates.applyFromMonth = String(
          Number.parseInt(newStates.applyFromMonth as string, 10)
        );
      }
      delete newStates.__typename;
      setStates(newStates);
    }
  }, [loading, result, setStates]);

  const handleChange = ({ key, value }: { key: string; value: any }) => {
    const newState = { ...states, [key]: value };
    setStates(newState);
  };

  const handleStateChange = (newState: typeof states) => {
    setStates(newState);
  };

  const { setOpenMessage } = useOcSnackbar();
  const [saveDeclaration, { loading: mutationLoading }] = useMutation(
    MY_TAX_CREATE_FIRST_MARRIED_PERSON_DECLARATION,
    {
      onCompleted: (data) => {
        const res = data?.createTaxDeclarationFirstMarriedPerson;
        setDisabledButton(false);
        if (res?.result) {
          if (res?.messages?.length > 0) {
            const message = res?.messages[0];
            if (
              message === "A korábban beadott nyilatkozata törlésre került!"
            ) {
              setOpenMessage({
                type: "success",
                message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
              });
              setOpenMessage({
                type: "warning",
                message: t("MY_TAX_PREVIOUS_TAX_DECLARATION_DELETED"),
              });
            }
          } else {
            setOpenMessage({
              type: "success",
              message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
            });
          }
          setSaveErrors([]);
          const params = {
            editable: false,
            tableId: res?.tableId,
            year: states?.dateOfDeclaration?.getFullYear().toString(),
            type: "TaxDeclarationFirstMarriedPerson",
            state: "requested",
          };
          setSelected(params);
        } else {
          setSaveErrors(res?.messages);
          setDisabledButton(false);
        }
      },
      onError: () => {
        setSaveErrors([t("MY_TAX_DECLARATION_CREATED_FAILED")]);
        setDisabledButton(false);
      },
    },
  );

  useEffect(() => {
    if (!states?.isModifiedDeclaration) {
      handleStateChange({ ...states, noApply: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states?.isModifiedDeclaration]);

  const handleSave = () => {
    setDisabledButton(true);
    const params = { ...states };
    if (params.applyFromMonth != null) {
      const monthNum = Number(params.applyFromMonth);
      params.applyFromMonth = monthNum < 10
        ? `0${monthNum}`
        : String(monthNum);
    }
    if (params.spouseEmployerTaxNumber) {
      params.spouseEmployerTaxNumber = params.spouseEmployerTaxNumber.replaceAll(/[-\s]/g, "");
    }
    saveDeclaration({ variables: params });
  };

  return {
    data: result,
    loading: loading || mutationLoading,
    error,
    states,
    disabledButton,
    resetStates,
    handleChange,
    handleStateChange,
    handleSave,
    saveErrors,
    setSaveErrors,
  };
};

type MyTaxFirstMarriageTaxCreditApplicationProps = {
  fullName: string;
  taxNumber: string;
  useMyHrSpousesData?: typeof useMyHrSpousesDataDI;
  taxApplicationSaveEnabled: boolean;
  handleRemoveRequest?: () => void;
  selected: any;
  setSelected: (params: any) => void;
  isMobile: boolean;
};

const MyTaxFirstMarriageTaxCreditApplication = ({
  fullName,
  taxNumber,
  useMyHrSpousesData = useMyHrSpousesDataDI,
  taxApplicationSaveEnabled,
  handleRemoveRequest,
  selected,
  setSelected,
  isMobile,
}: MyTaxFirstMarriageTaxCreditApplicationProps) => {
  const editable = selected?.editable;
  const history = useHistory();
  const theme = useTheme();

  const title =
    "Adóelőleg-nyilatkozat az első házasok kedvezményének érvényesítéséről ";

  const componentRef = useRef(null);

  const {
    states,
    loading,
    error,
    saveErrors,
    disabledButton,
    setSaveErrors,
    resetStates,
    handleChange,
    handleStateChange,
    handleSave,
  } = useFormData({
    _id: selected?.tableId,
    setSelected,
  });

  const isModified = selected?.isModified;
  if (isModified && !states?.isModifiedDeclaration) {
    handleStateChange({ ...states, isModifiedDeclaration: true });
  }
  const showModifiedButton = states?.state === "E" ? true : false;
  const showRemoveButton =
    states?.state === "K" || selected?.state === "K" ? true : false;

  const dateOfDeclarationString = new Date(
    states?.dateOfDeclaration,
  ).toLocaleDateString();

  const [marriageDate, setMarriageDate] = useState(states?.marriageDate);

  useEffect(() => {
    setMarriageDate(states?.marriageDate);
  }, [states?.marriageDate]);

  const changeMarriageDate = (newDate: Date | null) => {
    setMarriageDate(newDate);
    handleChange({
      key: "marriageDate",
      value: newDate ? new Date(newDate) : null,
    });
  };

  const print = useReactToPrint({
    content: () => componentRef.current,
  });

  const handlePrint = () => {
    print();
  };

  const { t, i18n } = useTranslation();

  const { data: spouses } = useMyHrSpousesData();

  useEffect(() => {
    if (editable) {
      if (spouses?.length > 0) {
        if (spouses[0].name !== states?.spouseName) {
          handleChange({ key: "spouseName", value: spouses[0].name });
        }
        if (spouses[0].taxNumber !== states?.spouseTaxNumber) {
          handleChange({ key: "spouseTaxNumber", value: spouses[0].taxNumber });
        }
      }
    }
  }, [
    editable,
    spouses,
    states?.spouseName,
    states?.spouseTaxNumber,
    handleChange,
  ]);

  const handleCancel = () => {
    // Only reset states when in edit mode
    if (editable) {
      resetStates();
      resetRadioGroupState();
    }
    setSaveErrors([]);
  };

  useEffect(() => {
    return () => {
      handleCancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let { contractCode, employeeCode } = useOcCurrentUserState();
  const { data: contractsData } = useMyHrContractsByEmployee({
    employee: employeeCode,
  });

  let selectedContract = contractsData?.find(
    (item: { code: string }) => item.code === contractCode,
  );

  let disabled = true;

  const handleModChange = ({ key, value }: { key: string; value: any }) => {
    handleChange({ key, value });
  };

  const [radioGroupState, setRadioGroupState] = useRecoilState(
    firstApplyInTaxYearOrToWithDrawal,
  );

  const resetRadioGroupState = useResetRecoilState(
    firstApplyInTaxYearOrToWithDrawal,
  );

  const handleRadioGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "applyInTaxYear") {
      const newStates = {
        ...states,
        applyInTaxYear: true,
        applyToWithdrawal: false,
      };
      handleStateChange(newStates);
    } else {
      const newStates = {
        ...states,
        applyToWithdrawal: true,
        applyInTaxYear: false,
      };
      handleStateChange(newStates);
    }
    setRadioGroupState(e.target.value);
  };

  if (!editable && states?.applyToWithdrawal) {
    setRadioGroupState("applyToWithdrawal");
  }

  const handleScrollToError = () => {
    const errorElement = document.getElementById("errorBox");
    if (errorElement) {
      window.scrollTo({
        top: 300,
        behavior: "smooth",
      });
    }
  };

  useLayoutEffect(() => {
    handleScrollToError();
  }, [saveErrors, history.action]);

  useEffect(() => {
    // Only reset when creating a NEW declaration (editable=true AND no tableId)
    if (editable && !selected?.tableId) {
      resetStates();
    }
    if (history.action === "POP" && editable) {
      return handleCancel();
    }
  }, []); //eslint-disable-line

  return (
    <>
      <Box
        style={{
          marginBottom: "-15px",
        }}
      >
        <Box
          sx={{
            display: "none",
            displayPrint: "block",
            paddingRight: "85px",
          }}
        >
          <MyTaxFirstMarriageTaxCreditApplicationPrint
            ref={componentRef}
            fullName={fullName}
            taxNumber={taxNumber}
            isModifiedApplication={states?.isModifiedDeclaration}
            // marriageDiscountContract prop removed as it does not exist in the target component's props
            marriageMounth={states?.applyFromMonth}
            marriageAmount={states?.requestAmount}
            noneMarriageDiscountContract={states?.noApply}
            states={states}
            employerName={selectedContract?.company?.name || ""}
            employerTaxNumber={selectedContract?.company?.taxNumber || ""}
            marriageDateDate={states?.marriageDate || ""}
            applicationDate={dateOfDeclarationString}
          />
        </Box>
        <MyTaxApplicationHeader
          title={title}
          onCancel={handleCancel}
          isMobile={isMobile}
        />
        <Box height="16px" />
        <Box display="flex" justifyContent="space-around">
          <Box
            style={{
              padding: "7px",
              width: "260px",
              borderRadius: "4px",
            }}
          >
            <OcDownload
              iconType="document"
              label="Tájékoztató"
              actionIconType="url"
              href={"#" + myTaxFirstMarriageTaxCreditInformationPageRoute}
              target="_blank"
              isSmall={true}
            />
          </Box>
        </Box>
        <Box height="16px" />
        {saveErrors.length > 0 && (
          <Box id="errorBox">
            <MyTaxCreditApplicationErrorBox error={saveErrors} />
          </Box>
        )}
        <OcCard
          label="I. A nyilatkozatot adó magánszemély"
          loading={loading}
          error={!!error}
        >
          <Box height="52px" />
          <MyTaxApplicationCardInfoItem
            label="Dátum"
            value={dateOfDeclarationString}
            isMobile={isMobile}
          />
          <MyTaxApplicationCardInfoItem
            label="Dolgozó neve"
            value={fullName}
            isMobile={isMobile}
          />
          <MyTaxApplicationCardInfoItem
            label="Adóazonosító jele"
            value={taxNumber}
            isMobile={isMobile}
          />
          <Box height="15px" />
          <MyTaxModifiedStatementCheck
            label={t("MY_TAX_MODIFIED_APPLICATION")}
            name="isModifiedDeclaration"
            value={states?.isModifiedDeclaration}
            onChange={handleModChange}
            disabled={!editable || isModified}
            isMobile={isMobile}
          />
          <Box height="15px" />
          <Box display="flex" flexDirection="row">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              1.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              Házastársam
            </Typography>
          </Box>
          <Box height="30px" />
          {spouses.length !== 0 ? (
            <MyTaxSpouseCollapse
              editable={editable}
              states={states}
              onChange={handleChange}
              isMobile={isMobile}
            />
          ) : (
            <ApplicationCollapseHeader
              dependentName={NOT_PROVIDED}
              dependentTaxNumber=""
              expanded={disabled}
              icon={humanMaleFemale}
              taxNumberLabel=""
              isMobile={isMobile}
              handleExpandClick={() => { }}
            />
          )}
          <Box height="30px" />
          <Box
            display="flex"
            alignItems={isMobile ? "flex-start" : "center"}
            flexDirection={isMobile ? "column" : "row"}
          >
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              A házasságkötés ideje, vagy a 25 év alatti fiatalok házasságkötése
              esetén a házastárs 25. életéve betöltésének időpontja:
            </Typography>
            {isMobile && <Box height="16px" />}
            <Box id="marriageDate">
              <Box marginLeft={isMobile ? "0px" : "30px"} width="200px">
                <OcDatePicker
                  id="marriage-date-picker"
                  label={t("OC_DATE")}
                  value={marriageDate ? new Date(marriageDate) : null}
                  onChange={(date) => changeMarriageDate(date)}
                  format={getDateFormatByLocale(i18n.language)}
                  disabled={!editable}
                />
              </Box>
            </Box>
          </Box>
          <Box height="30px" />
          <Box display="flex" flexDirection="row">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              2.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              Tudomásul veszem, hogy az első házasok kedvezménye maximum 24
              jogosultsági hónapon keresztül vehető figyelembe.
            </Typography>
          </Box>
          <Box height="26px" />
          <Box marginLeft="32px">
            <FormControl
              component="fieldset"
              sx={{ display: "flex" }}
            >
              <Box height="8px" />
              <RadioGroup
                value={radioGroupState}
                aria-label="gender"
                name="controlled-radio-buttons-group"
                onChange={handleRadioGroupChange}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  color={
                    editable
                      ? theme.palette.darkBase.main
                      : theme.palette.gray60.main
                  }
                >
                  2.1.
                  <Box width="16px" />
                  <FormControlLabel
                    value="applyInTaxYear"
                    control={
                      <Radio
                        id="applyInTaxYear"
                        style={{
                          color: !editable
                            ? theme.palette.gray60.main
                            : theme.palette.darkBase.main,
                        }}
                        disabled={!editable}
                      />
                    }
                    label={
                      <Typography
                        variant="body1"
                        color={editable ? "primary" : "info"}
                        style={{ alignItems: "center" }}
                      >
                        Nyilatkozatomat az adóévben, legfeljebb a jogosultsági
                        időszak végéig kérem figyelembe venni.
                      </Typography>
                    }
                  />
                </Box>
                <Box height="3px" />
                <Box
                  display="flex"
                  alignItems="center"
                  color={
                    editable
                      ? theme.palette.darkBase.main
                      : theme.palette.gray60.main
                  }
                >
                  2.2.
                  <Box width="16px" />
                  <FormControlLabel
                    value="applyToWithdrawal"
                    control={
                      <Radio
                        id="applyToWithdrawal"
                        style={{
                          color: !editable
                            ? theme.palette.gray60.main
                            : theme.palette.darkBase.main,
                        }}
                        disabled={!editable}
                      />
                    }
                    label={
                      <Typography
                        variant="body1"
                        color={editable ? "primary" : "info"}
                        style={{ alignItems: "center" }}
                      >
                        Nyilatkozatomat visszavonásig, legfeljebb a jogosultsági
                        időszak végéig kérem figyelembe venni (folytatólagos
                        nyilatkozatot teszek).
                      </Typography>
                    }
                  />
                </Box>
              </RadioGroup>
            </FormControl>
          </Box>
          <Box height="36px" />
          <Box display="flex" flexDirection="row">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              3.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              Nyilatkozom, hogy
            </Typography>
          </Box>
          <Box height="16px" />
          <Box
            display="inline-flex"
            alignItems={isMobile ? "flex-start" : "center"}
            flexDirection={isMobile ? "column" : "row"}
            style={{
              marginLeft: isMobile ? "0px" : "15px",
            }}
          >
            <Box
              width="200px"
              marginLeft={isMobile ? "0px" : "16px"}
              marginRight="16px"
              display="flex"
              flexDirection="row"
              style={{
                backgroundColor: "white",
                borderRadius: "4px",
              }}
            >
              <OcMonthSelect
                id="start-of-application-select"
                label="Hó"
                selectedMonth={states?.applyFromMonth || ""}
                onSelectMonth={(e) =>
                  handleChange({ key: "applyFromMonth", value: e })
                }
                width="200px"
                disabled={!editable}
                monthList={[
                  { index: "1", name: "Január" },
                  { index: "2", name: "Február" },
                  { index: "3", name: "Március" },
                  { index: "4", name: "Április" },
                  { index: "5", name: "Május" },
                  { index: "6", name: "Június" },
                  { index: "7", name: "Július" },
                  { index: "8", name: "Augusztus" },
                  { index: "9", name: "Szeptember" },
                  { index: "10", name: "Október" },
                  { index: "11", name: "November" },
                  { index: "12", name: "December" },
                ]}
                startMonth={1}
              />
            </Box>
            <Typography
              variant="body1"
              color={editable ? "primary" : "info"}
              style={{
                marginLeft: isMobile ? "0px" : "6px",
              }}
            >
              hónaptól kezdődően (e hónapra is) az első házasok kedvezményét
            </Typography>
          </Box>
          <Box height="16px" />
          <Box marginLeft={isMobile ? "0px" : "16px"}>
            <MyTaxCreditApplicationCheckboxLine
              id="marriageAmount"
              text="forint összegben kívánom igénybe venni."
              textInput={true}
              textInputType="number"
              value={states?.requestAmount === 0 ? "" : states?.requestAmount}
              label={t("OC_AMOUNT")}
              onChangeInput={(e) =>
                handleChange({
                  key: "requestAmount",
                  value: Number.parseFloat(e.target.value.replaceAll(/[^0-9]/g, "")),
                })
              }
              textInputDisabled={!editable}
              isMobile={isMobile}
              editable={editable}
              checked={false}
            />
          </Box>
          <Box height="36px" />
          <MyTaxCreditApplicationCheckboxLine
            id="noneMarriageDiscountContract"
            type="checkbox"
            prefix="4."
            checked={states?.noApply}
            onChange={(e) =>
              handleChange({ key: "noApply", value: e.target.checked })
            }
            text="A nyilatkozattétel időpontjától kezdődően (erre a hónapra
                  sem) az első házasok kedvezményét nem kívánom igénybe venni."
            checkboxDisabled={!states?.isModifiedDeclaration || !editable}
            editable={editable}
          />
          <Box height="36px" />
          <Typography variant="body1" color={editable ? "primary" : "info"}>
            Felelősségem tudatában kijelentem, hogy a nyilatkozat tartalmát
            házastársam megismerte és elfogadta.
          </Typography>
          <Box height="26px" />
        </OcCard>
        <Box height="16px" />
        <TaxCreditApplicationButtonBars
          taxApplicationSaveEnabled={editable && taxApplicationSaveEnabled}
          cancelData={handleCancel}
          handlePrint={handlePrint}
          saveData={handleSave}
          editable={editable}
          disabledButton={disabledButton}
          handleRemoveRequest={handleRemoveRequest ?? (() => { })}
          showModifiedButton={showModifiedButton}
          showRemoveButton={showRemoveButton}
          isModified={isModified}
          isModifiedDeclaration={states?.isModifiedDeclaration}
          isMobile={isMobile}
        />
      </Box>
    </>
  );
};

export default MyTaxFirstMarriageTaxCreditApplication;
